import React from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { useRole } from '../../hooks/useRole';
import { roleLevel } from '../../utils/roles';
import type { MenuItem } from '../../constants/sidebarNavGroups';
import { isNavFlagEnabled, isNavHidden, isNavPinned, menuSections, sanitizeHiddenMenu, setNavHidden } from '../../utils/sidebarMenu';

const FINANCIAL_ITEM_IDS = new Set(['budget', 'budget-variances', 'quotes', 'payments']);

/**
 * Settings > Menu: which modules this person sees in the left-hand menu. Saved with the user's profile as
 * `settings.menu.hidden`. A hidden module is only left out of the menu: its pages stay reachable by address and
 * search, and nobody gains or loses access.
 */
const MenuSettingsCard: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const { role, canViewFinancials } = useRole();
  const level = roleLevel(role);
  const hidden = sanitizeHiddenMenu(settings.menu?.hidden);

  // Only entries this person can see in the menu are offered; the choice for any other would change nothing.
  const offered = (item: MenuItem): boolean =>
    isNavFlagEnabled(item.id)
    && (!FINANCIAL_ITEM_IDS.has(item.id) || canViewFinancials)
    && (!item.minRole || level >= roleLevel(item.minRole));

  const save = (next: string[]) => updateSettings({ menu: { hidden: next } });

  const renderEntry = (item: MenuItem, nested = false) => {
    const pinned = isNavPinned(item.id);
    const shown = pinned || !isNavHidden(item.id, hidden);
    return (
      <li key={item.id} className={nested ? 'menu-settings-entry menu-settings-entry--nested' : 'menu-settings-entry'}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={shown}
            disabled={pinned}
            onChange={e => save(setNavHidden(hidden, item.id, !e.target.checked))}
          />
          {item.label}
        </label>
        {pinned && <span className="menu-settings-note">Always shown</span>}
      </li>
    );
  };

  return (
    <div className="settings-card">
      <h4 className="settings-card-title">Menu</h4>
      <p className="menu-settings-help">
        Choose which modules appear in your left-hand menu. Hidden modules can still be opened by address or search, and your access does not change.
      </p>
      {menuSections().map(section => {
        const items = section.items.filter(offered);
        if (items.length === 0) return null;
        return (
          <div key={section.title} className="menu-settings-section">
            <h5 className="menu-settings-section-title">{section.title}</h5>
            <ul className="menu-settings-list">
              {items.map(item => (
                <React.Fragment key={item.id}>
                  {renderEntry(item)}
                  {(item.children ?? []).filter(offered).map(child => renderEntry(child, true))}
                </React.Fragment>
              ))}
            </ul>
          </div>
        );
      })}
      <button type="button" className="btn btn-secondary btn-sm" disabled={hidden.length === 0} onClick={() => save([])}>
        Show all
      </button>
    </div>
  );
};

export default MenuSettingsCard;
